import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// project imports
import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import useAuth from 'contexts/AuthContext';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';

// The template used a stock photo of a random person here, which read as
// "this is your account" while showing a stranger. Initials instead —
// there's no avatar upload, and inventing a face is worse than not having one.
function initialsOf(user) {
  if (!user) return '';
  const initials = [user.first_name, user.last_name]
    .filter(Boolean)
    .map((part) => part[0])
    .join('');
  return (initials || user.email?.[0] || '').toUpperCase();
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //
// The template shipped ten menu items here — Edit Profile, Social Profile,
// Billing, Privacy Center, History and so on — none of which did anything.
// Replaced with the two actions that exist.

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const displayName = user ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email : '';
  const roleLabel = user?.role === 'admin' ? 'Administrator' : 'Student';

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const goToAccount = () => {
    setOpen(false);
    navigate('/account');
  };

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 'auto' }}>
      <ButtonBase
        sx={(theme) => ({
          p: 0.25,
          borderRadius: 1,
          '&:focus-visible': { outline: `2px solid ${theme.vars.palette.secondary.dark}`, outlineOffset: 2 }
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Avatar
          alt={displayName}
          size="sm"
          sx={{ bgcolor: 'primary.main', color: 'common.white', '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } }}
        >
          {initialsOf(user)}
        </Avatar>
      </ButtonBase>

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.vars.customShadows.z1, width: 260 })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                    <Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
                      <Avatar alt={displayName} sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'common.white' }}>
                        {initialsOf(user)}
                      </Avatar>
                      <Stack sx={{ minWidth: 0 }}>
                        <Typography variant="h6" noWrap>
                          {displayName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {roleLabel}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>

                  <List component="nav" sx={{ p: 0, pb: 1, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                    <ListItemButton onClick={goToAccount}>
                      <ListItemIcon>
                        <UserOutlined />
                      </ListItemIcon>
                      <ListItemText primary="My account" />
                    </ListItemButton>
                    <ListItemButton onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Sign out" />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
